import type { ReadingSlide } from "../ui/StartReader";
import { AppChip, C, Frame, MONO } from "../ui/readingArt";
import { tx, txv, txr } from "../lib/tx";

/**
 * "Read while you wait" summary for the inside-the-bank start screen. Three
 * short slides (what/who, how one password opens the door, key terms) shown in
 * the reader while the 3D room downloads. Content only; the reader UI lives in
 * ui/StartReader.tsx and the shared art toolkit in ui/readingArt.tsx.
 */

/* ------------------------------------------------------------------ */
/* 1. a leaked password and the same words with a new year            */
/* ------------------------------------------------------------------ */
function LeakPatternArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label={tx("Example: one pattern, two accounts")} aria={tx("A leaked streaming password, BestSummer2025, and a work mailbox sign-in using the same words with a new year")} height={H}>
      <rect x="16.5" y="36.5" width="327" height="84" rx="8" fill={C.panel2} stroke={C.line} />
      <text x="30" y="55" fontSize="10.5" fill={C.muted}>
        {tx("Streaming service leak")}
      </text>
      <rect className="sr-anim-pulse" x="278" y="43" width="54" height="18" rx="9" fill={C.red} fillOpacity="0.12" stroke={C.red} strokeOpacity="0.45" />
      <text x="305" y="55.5" fontSize="9.5" fontWeight="700" fill={C.red} textAnchor="middle" letterSpacing="0.6">
        {tx("LEAKED")}
      </text>
      <line x1="17" y1="67.5" x2="343" y2="67.5" stroke={C.line} />
      <text x="30" y="85" fontSize="11" fontFamily={MONO} fill={C.muted}>
        alex.morgan@bank.example
      </text>
      <text x="30" y="106" fontSize="12.5" fontFamily={MONO} fill={C.soft}>
        {tx("BestSummer")}<tspan fill={C.amber}>2025</tspan>!
      </text>
      <text x="330" y="106" fontSize="10.5" fill={C.muted} textAnchor="end">
        {tx("password")}
      </text>

      <g className="sr-anim-float">
        <line x1="40" y1="128" x2="40" y2="148" stroke={C.amber} strokeWidth="1.6" strokeLinecap="round" />
        <polyline points="35,143 40,149 45,143" fill="none" stroke={C.amber} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      <text x="56" y="142" fontSize="11" fontWeight="600" fill={C.amber}>
        {tx("Same words, new year")}
      </text>

      <rect x="16.5" y="158.5" width="327" height="124" rx="8" fill={C.panel2} stroke={C.line} />
      <AppChip x={28} y={168} />
      <text x="62" y="185" fontSize="13" fontWeight="700" fill={C.text}>
        {tx("Work mailbox")}
      </text>
      <text x="330" y="185" fontSize="10.5" fill={C.muted} textAnchor="end">
        {tx("Sign in")}
      </text>
      <line x1="17" y1="203.5" x2="343" y2="203.5" stroke={C.line} />
      <rect x="28.5" y="211.5" width="303" height="26" rx="6" fill={C.code} stroke={C.line} />
      <text x="40" y="228" fontSize="11" fontFamily={MONO} fill={C.muted}>
        alex.morgan@bank.example
      </text>
      <rect x="28.5" y="245.5" width="200" height="26" rx="6" fill={C.code} stroke={C.mint} strokeOpacity="0.5" />
      <text x="40" y="262" fontSize="11.5" fontFamily={MONO} fill={C.text}>
        {tx("BestSummer")}<tspan fill={C.amber}>2026</tspan>!
      </text>
      <rect className="sr-anim-blink" x="147" y="251" width="1.5" height="14" rx="0.75" fill={C.mint} />
      <rect x="238.5" y="245.5" width="93" height="26" rx="6" fill={C.mint} fillOpacity="0.14" stroke={C.mint} strokeOpacity="0.45" />
      <text x="285" y="262" fontSize="11.5" fontWeight="700" fill={C.mint} textAnchor="middle">
        {tx("Sign in")}
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. fake sign-in page + public clues assembled into a guess         */
/* ------------------------------------------------------------------ */
function FakePageArt(): JSX.Element {
  const H = 300;
  return (
    <Frame label={tx("Example: a fake page and public clues")} aria={tx("A look-alike sign-in page with a wrong character in its address, and a pet name and birth year from a public profile combined into a password guess")} height={H}>
      <rect x="16.5" y="36.5" width="327" height="128" rx="8" fill={C.code} stroke={C.line} />
      <rect x="26.5" y="44.5" width="307" height="22" rx="11" fill={C.panel2} stroke={C.line} />
      <text x="40" y="59.5" fontSize="10.5" fontFamily={MONO} fill={C.muted}>{txr("https://bank-sa{tspan}ary.example", { tspan: <tspan fill={C.red} fontWeight="700">1</tspan> })}
      </text>
      <circle className="sr-anim-ring" cx="136" cy="55.5" r="7" fill="none" stroke={C.red} strokeWidth="1.4" />
      <text x="30" y="87" fontSize="12.5" fontWeight="700" fill={C.text}>
        {tx("Salary update: sign in to view")}
      </text>
      <rect x="28.5" y="95.5" width="303" height="22" rx="6" fill={C.panel2} stroke={C.line} />
      <text x="38" y="110" fontSize="10.5" fontFamily={MONO} fill={C.muted}>
        alex.morgan@bank.example
      </text>
      <rect x="28.5" y="125.5" width="200" height="22" rx="6" fill={C.panel2} stroke={C.red} strokeOpacity="0.5" />
      <text x="38" y="140" fontSize="11" fontFamily={MONO} fill={C.soft} letterSpacing="1.5">
        {"\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"}
      </text>
      <rect className="sr-anim-blink" x="140" y="129.5" width="1.5" height="14" rx="0.75" fill={C.red} />
      <rect x="238.5" y="125.5" width="93" height="22" rx="6" fill={C.red} fillOpacity="0.12" stroke={C.red} strokeOpacity="0.4" />
      <text x="285" y="140" fontSize="11" fontWeight="700" fill={C.red} textAnchor="middle">
        {tx("View")}
      </text>
      <text x="30" y="158" fontSize="10" fill={C.muted}>
        {tx("Looks right. The address is one character off.")}
      </text>

      <text x="16" y="187" fontSize="10.5" fill={C.muted}>
        {tx("Public profile")}
      </text>
      <rect x="16.5" y="194.5" width="104" height="26" rx="13" fill={C.panel2} stroke={C.line} />
      <text x="68.5" y="211.5" fontSize="11.5" fill={C.text} textAnchor="middle">
        {tx("Pet: Biscuit")}
      </text>
      <rect x="130.5" y="194.5" width="104" height="26" rx="13" fill={C.panel2} stroke={C.line} />
      <text x="182.5" y="211.5" fontSize="11.5" fill={C.text} textAnchor="middle">
        {tx("Born: 2004")}
      </text>
      <line x1="68" y1="222" x2="68" y2="238" stroke={C.amber} strokeWidth="1.4" strokeLinecap="round" />
      <line x1="182" y1="222" x2="182" y2="238" stroke={C.amber} strokeWidth="1.4" strokeLinecap="round" />

      <rect x="16.5" y="238.5" width="327" height="48" rx="8" fill={C.amber} fillOpacity="0.1" stroke={C.amber} strokeOpacity="0.4" />
      <text x="30" y="256" fontSize="10.5" fill={C.muted}>
        {tx("Guessing tool tries")}
      </text>
      <text x="30" y="276" fontSize="14" fontWeight="700" fontFamily={MONO} fill={C.amber}>
        {tx("Biscuit2004")}
      </text>
      <rect className="sr-anim-pulse" x="270" y="260" width="62" height="18" rx="9" fill={C.amber} fillOpacity="0.14" stroke={C.amber} strokeOpacity="0.45" />
      <text x="301" y="272.5" fontSize="9.5" fontWeight="700" fill={C.amber} textAnchor="middle" letterSpacing="0.6">
        {tx("GUESS")}
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. glossary: one row per term                                       */
/* ------------------------------------------------------------------ */
type Glyph = "leak" | "reuse" | "fake" | "clue" | "tool" | "unique";

function GlyphShape({ g }: { g: Glyph }): JSX.Element {
  const s = { fill: "none", stroke: C.mint, strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" } as const;
  switch (g) {
    case "leak":
      return (
        <g {...s}>
          <ellipse cx="0" cy="-5" rx="6.5" ry="2.6" />
          <path d="M-6.5 -5 V5 a6.5 2.6 0 0 0 13 0 V-5" />
          <path d="M-6.5 0 a6.5 2.6 0 0 0 13 0" />
        </g>
      );
    case "reuse":
      return (
        <g {...s}>
          <rect x="-9" y="-6" width="11" height="12" rx="2" />
          <rect x="-2" y="-6" width="11" height="12" rx="2" />
        </g>
      );
    case "fake":
      return (
        <g {...s}>
          <rect x="-9" y="-7" width="18" height="14" rx="2.5" />
          <line x1="-9" y1="-3" x2="9" y2="-3" />
          <line x1="0" y1="0" x2="0" y2="2" />
        </g>
      );
    case "clue":
      return (
        <g {...s}>
          <path d="M-9 0 q9 -9 18 0 q-9 9 -18 0 z" />
          <circle cx="0" cy="0" r="2.4" />
        </g>
      );
    case "tool":
      return (
        <g {...s}>
          <rect x="-9" y="-7" width="18" height="14" rx="2.5" />
          <polyline points="-5,-2.5 -1.5,0 -5,2.5" />
          <line className="sr-anim-blink" x1="1" y1="3" x2="5" y2="3" />
        </g>
      );
    case "unique":
      return (
        <g {...s}>
          <circle cx="-4" cy="0" r="3.8" />
          <line x1="-0.2" y1="0" x2="9" y2="0" />
          <line x1="5.6" y1="0" x2="5.6" y2="3.4" />
          <line x1="9" y1="0" x2="9" y2="3.4" />
        </g>
      );
  }
}

interface RowSpec {
  g: Glyph;
  title: string;
  sub: string;
}

const ROWS: RowSpec[] = [
  { g: "leak", title: "Leaked password", sub: "from another site" },
  { g: "reuse", title: "Pattern reuse", sub: "same words, new year" },
  { g: "fake", title: "Fake sign-in page", sub: "look-alike address" },
  { g: "clue", title: "Public clues", sub: "pets, dates, names" },
  { g: "tool", title: "Guessing tool", sub: "mixes known facts" },
  { g: "unique", title: "Unique password", sub: "one per account" },
];

function TermsArt(): JSX.Element {
  const H = 298;
  return (
    <Frame label={tx("Terms at a glance")} aria={tx("Six terms as labelled rows: leaked password, pattern reuse, fake sign-in page, public clues, guessing tool and unique password")} height={H}>
      {ROWS.map((r, i) => (
        <g key={r.g} transform={`translate(16 ${36 + i * 43})`}>
          <rect x="0.5" y="0.5" width="327" height="37" rx="10" fill={C.panel2} stroke={C.line} />
          <circle cx="23" cy="19" r="13" fill={C.mint} fillOpacity="0.12" />
          <g transform="translate(23 19)">
            <GlyphShape g={r.g} />
          </g>
          <text x="46" y="23.5" fontSize="12.5" fontWeight="700" fill={C.text}>
            {txv(r.title)}
          </text>
          <text x="315" y="23" fontSize="10.5" fill={C.muted} textAnchor="end">
            {txv(r.sub)}
          </text>
        </g>
      ))}
    </Frame>
  );
}

export const INSIDE_THE_BANK_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "One password can open more than one door.",
    illustration: LeakPatternArt,
    caption: "A new year on an old password is still the old password.",
    sections: [
      {
        h: "What this is about",
        body: "You play the attacker. From one leaked password, you try to get into an employee\u2019s work mailbox at a fictional bank.",
      },
      {
        h: "Who it\u2019s for",
        body: "Everyone who signs in to work accounts. No technical background needed.",
      },
      {
        h: "Why your judgement matters",
        body: "No system can see which passwords you reuse or what you type into a page. Only you decide whether a sign-in is real, and whether a password is truly new.",
      },
    ],
  },
  {
    tab: "Main risks",
    headline: "How a leak turns into a break-in.",
    illustration: FakePageArt,
    caption: "Typing into the page is the moment it is lost.",
    sections: [
      {
        h: "Old leaks get reused",
        body: "A password from one leaked service is tried on the work account. Changing the year or a symbol does not make it new.",
      },
      {
        h: "A fake sign-in page",
        body: "A salary message sends you to a look-alike page. Opening it costs nothing; typing your password into it does.",
      },
      {
        h: "Public facts become guesses",
        body: "A pet name and a birth year on a public profile are building blocks for a guessing tool.",
      },
      {
        h: "Strong is not safe once typed",
        body: "A long random password stops guessing. It does not stop you handing it over.",
      },
    ],
  },
  {
    tab: "Key terms",
    headline: "The words you\u2019ll meet today.",
    illustration: TermsArt,
    caption: "Short definitions \u2014 refer back any time.",
    sections: [
      { h: "Leaked password", body: "A password exposed when another website was breached, now circulating among attackers." },
      { h: "Pattern reuse", body: "Keeping the same words and only changing a year or symbol. The pattern is what gets guessed." },
      { h: "Fake sign-in page", body: "A look-alike page that collects whatever you type into it." },
      { h: "Public clues", body: "Pet names, dates and places anyone can read on a public profile." },
      { h: "Guessing tool", body: "Software that mixes known facts and leaked words into likely passwords and tries them." },
      { h: "Unique password", body: "Made from scratch for one account only, such as random unrelated words." },
    ],
  },
];
